import type { PlanRect } from '@/components/studio/plan/PlanTakeover';
import { chipById, type ChipId } from './chips';
import type { DraftFields } from './draft';

/** What the landing page (front-door-landing/hero.js) posts when the person sends their words. */
export interface StartMessage {
  prompt: string;
  chip: unknown;
  /** The idea last clicked, even if its words were then changed. */
  picked: ChipId | null;
  from: PlanRect | null;
  /** The notice was showing when they sent, so what they sent may be recorded. */
  notice: boolean;
}

const MAX_WORDS = 4000;

const isRect = (r: unknown): r is PlanRect =>
  !!r && typeof r === 'object' && ['left', 'top', 'width', 'height'].every((k) => Number.isFinite((r as Record<string, unknown>)[k]));

export function parseStartMessage(data: unknown): StartMessage | null {
  if (!data || typeof data !== 'object') return null;
  const m = data as Record<string, unknown>;
  if (m.type !== '4k:start' || typeof m.words !== 'string') return null;
  const prompt = m.words.trim().slice(0, MAX_WORDS);
  if (!prompt) return null;
  return { prompt, chip: m.chip, picked: chipById(m.picked)?.id ?? null, from: isRect(m.from) ? m.from : null, notice: m.notice === true };
}

/** An idea clicked in the landing page's box: its id, when it is one of ours. */
export function parsePickMessage(data: unknown): ChipId | null {
  if (!data || typeof data !== 'object') return null;
  const m = data as Record<string, unknown>;
  return m.type === '4k:pick' ? (chipById(m.chip)?.id ?? null) : null;
}

let from: PlanRect | null = null;

/** The box the words were typed in, so /start can slide them in from there. Same page only, never stored. */
export const handOffFrom = (rect: PlanRect | null): void => {
  from = rect;
};

export const takeHandOffFrom = (): PlanRect | null => {
  const rect = from;
  from = null;
  return rect;
};

/**
 * What a front-door start hands the video workspace in its history state (`frontDoor`), so the studio can show the
 * plan /start showed: planFor({ prompt, ...frontDoor }). The draft itself is gone by the time the studio opens.
 */
export type FrontDoorStart = Pick<DraftFields, 'chip' | 'look'>;
